import { useState } from "react";
import { Eye, MessageSquare } from "lucide-react";
import { cn } from "@ui";
import { SelectionCheckbox } from "./selection-checkbox";
import { RevealDialog } from "../insiti-link/reveal-dialog";

declare const Turbo: {
  visit: (url: string, options?: { frame?: string }) => void;
};

function messageUrl(ids: number[]): string {
  return `/app/messages/new?${ids.map((id) => `recipient_ids[]=${id}`).join("&")}`;
}

interface SelectionBarProps {
  allChecked: boolean;
  indeterminate: boolean;
  count: number;
  recipientIds: number[];
  onToggleAll: () => void;
}

// Persistent bar above selectable tables: select-all + label on the left,
// batch actions on the right (disabled until at least one row is selected).
// "Afficher les coordonnées" opens the React reveal dialog (stacks above the
// expand dialog); "Envoyer un message" opens the Turbo message drawer.
export function SelectionBar({
  allChecked,
  indeterminate,
  count,
  recipientIds,
  onToggleAll,
}: SelectionBarProps) {
  const anySelected = count > 0;
  const [revealOpen, setRevealOpen] = useState(false);

  const sendMessage = () =>
    Turbo.visit(messageUrl(recipientIds), { frame: "drawer" });

  const actionBtn = (enabled: boolean, primary: boolean) =>
    cn(
      "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm transition-colors",
      primary ? "text-white" : "ring-1",
      !enabled && "cursor-not-allowed opacity-40",
      enabled && primary && "bg-default-600 hover:bg-default-700",
      !enabled && primary && "bg-default-600",
      enabled && !primary && "ring-default-200 text-slate-700 hover:bg-default-100",
      !enabled && !primary && "ring-default-200 text-slate-700",
    );

  return (
    <>
      <div
        className={cn(
          "mb-3 flex items-center gap-3 rounded-lg px-3 py-2 ring-1 transition-colors",
          anySelected ? "bg-default-50 ring-default-200" : "bg-slate-50 ring-slate-200",
        )}
      >
        <SelectionCheckbox
          checked={allChecked}
          indeterminate={indeterminate}
          onChange={onToggleAll}
          ariaLabel="Tout sélectionner"
        />
        <span
          className={cn(
            "text-sm font-medium",
            anySelected ? "text-default-700" : "text-slate-500",
          )}
        >
          {anySelected ? `${count} sélectionné${count > 1 ? "s" : ""}` : "Sélectionner"}
        </span>

        <div className="ml-auto flex gap-2">
          <button
            type="button"
            disabled={!anySelected}
            onClick={sendMessage}
            className={actionBtn(anySelected, true)}
          >
            <MessageSquare className="h-4 w-4" /> Envoyer un message
          </button>
          <button
            type="button"
            disabled={!anySelected}
            onClick={() => setRevealOpen(true)}
            className={actionBtn(anySelected, false)}
          >
            <Eye className="h-4 w-4" /> Afficher les coordonnées
          </button>
        </div>
      </div>

      {revealOpen && (
        <RevealDialog
          open={revealOpen}
          onClose={() => setRevealOpen(false)}
          userIds={recipientIds}
        />
      )}
    </>
  );
}
